import { FC, useMemo } from 'react';
import { dateFormatter } from '@app/shared/formatters';
import classNames from 'classnames';
import styles from '../sponsor-irr-and-hurdle.module.scss';
import SvgChevron from '@app/shared/icons/Chevron';
import { useAppSelector } from '@app/core/hooks/redux-hooks';
import MultipleRow from './MultipleRow';
import { useExpansionState } from '@app/shared/hooks/useExpansionState';
import { Pwerm2CalculationWaterfallCaseYearDto } from '@app/shared/models/contracts/pwerm2-calculation-results-dto';
import { setSponsorIrrTableExpandMultiples } from '@app/core/store/ui-values-slice';
import { EventColumn } from './SponsorIRRTable';
import { useFocusMode } from '@app/shared/hooks/focus-mode';
import { SponsorIrrFocusSelections } from '@app/shared/hooks/focus-mode/focus-mode-types';

interface ForecastYearSectionProps {
  forecastYearResults: Pwerm2CalculationWaterfallCaseYearDto;
  forecastYear: string;
  eventColumns: EventColumn[];
  isCurrentYearExpanded: boolean;
  onToggle: (forecastYearId: string) => void;
  lastRowRef?: (el: HTMLTableRowElement | null) => void;
}

const PARENT_CLASSNAME = 'sponsor-irr-table';

const ForecastYearSection: FC<ForecastYearSectionProps> = ({
  forecastYearResults,
  forecastYear,
  eventColumns,
  isCurrentYearExpanded,
  onToggle,
  lastRowRef,
}) => {
  const { isActive, focusType, selections } = useFocusMode();
  const focusModeType = isActive ? focusType : undefined;
  const sponsorSelections = selections as SponsorIrrFocusSelections | undefined;
  const selectedMultiple = sponsorSelections?.multiple;
  const selectedYear = sponsorSelections?.year;

  const storedExpandedIds = useAppSelector((state) => state.uiValues.sponsorIrrExpandMultiples);

  const multiples = forecastYearResults.multiples;

  const itemIds = useMemo(
    () => Object.keys(multiples).map((key) => `${forecastYear}-${key}`),
    [multiples, forecastYear]
  );

  const { isExpanded: isMultipleExpanded, handleToggle } = useExpansionState({
    itemIds,
    storedExpandedIds,
    saveExpandedIds: setSponsorIrrTableExpandMultiples,
    defaultExpanded: false,
    focusedItemId: isActive && selectedYear ? `${selectedYear}-${selectedMultiple}` : null,
  });

  return (
    <>
      <tr>
        <th
          className={classNames(
            'table-primary__cell--header',
            'table-primary__cell--right',
            styles[`${PARENT_CLASSNAME}__cell--no-padding-right`]
          )}>
          <div
            className={styles['expand-toggle']}
            style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <SvgChevron
              onClick={selectedMultiple ? undefined : () => onToggle(forecastYear)}
              className={classNames(
                isCurrentYearExpanded ? styles['chevron-expanded'] : styles['chevron-collapsed'],
                { [styles['greyed-out']]: focusModeType }
              )}
            />
            {dateFormatter(forecastYear, { month: 'short', year: '2-digit' })}
          </div>
        </th>
        <td className="table-primary__cell--header" colSpan={eventColumns.length + 1} />
      </tr>
      {isCurrentYearExpanded &&
        Object.entries(multiples).map(([multipleId, multipleData]) => (
          <MultipleRow
            key={multipleId}
            multipleId={multipleId}
            multipleData={multipleData}
            eventColumns={eventColumns}
            lastRowRef={lastRowRef}
            forecastYear={forecastYear}
            onToggle={() => handleToggle(`${forecastYear}-${multipleId}`)}
            isExpanded={isMultipleExpanded(`${forecastYear}-${multipleId}`)}
          />
        ))}
    </>
  );
};

export default ForecastYearSection;
